// pages/ArchivesPage.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';
import { useEmployeeDetail } from '../hooks/useEmployeeDetail';
import { deleteEmployeeFile, uploadEmployeeFile, type EmployeeFileCategory } from '../api/employees';
import api from '../api/client';

const ArchivesPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const numericId = id ? Number(id) : undefined;
    const { employee, files, loading, error, refresh } = useEmployeeDetail(numericId);

    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [newFile, setNewFile] = useState<File | null>(null);
    const [newLabel, setNewLabel] = useState('');
    const [newCategory, setNewCategory] = useState<EmployeeFileCategory>('OTHER');

    // Documents legacy (single-file fields)
    const legacyArchives = React.useMemo(() => {
        return [
            { field: 'identity_document', label: "Pièce d'identité", url: employee?.identity_document, size: employee?.identity_document_size, date: employee?.identity_document_date },
            { field: 'birth_certificate', label: 'Extrait de naissance', url: employee?.birth_certificate, size: employee?.birth_certificate_size, date: employee?.birth_certificate_date },
            { field: 'other_documents', label: 'Autres documents', url: employee?.other_documents, size: employee?.other_documents_size, date: employee?.other_documents_date },
        ].filter(d => !!d.url);
    }, [employee]);

    // Détection PDF via extension
    function isPdfUrl(url?: string | null) {
        if (!url) return false;
        try {
            const u = new URL(url, window.location.origin);
            return u.pathname.toLowerCase().endsWith('.pdf');
        } catch {
            return false;
        }
    }

    const handleOpenOrPrint = (url?: string | null) => {
        if (!url) return;
        const isPdf = isPdfUrl(url);
        const w = window.open(url, '_blank');
        if (!w) return;
        if (isPdf) {
            const interval = setInterval(() => {
                try {
                    if (w.document?.readyState === 'complete') {
                        clearInterval(interval);
                        w.focus();
                        w.print();
                    }
                } catch {
                    // cross-origin pendant chargement
                }
            }, 400);
        }
    };

    const handleDeleteArchive = async (field: string) => {
        if (!employee) return;
        if (!confirm('Supprimer définitivement ce fichier ?')) return;
        try {
            await api.post(`employees/employees/${employee.id}/delete_file/`, { field });
            await refresh();
        } catch (e) {
            console.error('Suppression fichier échouée:', e);
            alert('Impossible de supprimer ce fichier.');
        }
    };

    const handleUpload = async () => {
        if (!employee || !newFile) return;
        try {
            setUploading(true);
            await uploadEmployeeFile(employee.id, newFile, { label: newLabel || undefined, category: newCategory });
            setNewFile(null);
            setNewLabel('');
            setNewCategory('OTHER');
            await refresh();
        } catch (e: any) {
            console.error('Upload pièce jointe échoué:', e);
            alert("L'upload a échoué. Vérifiez que le fichier est un PDF < 100MB.");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAttached = async (fileId: number, label: string) => {
        if (!employee) return;
        if (!confirm(`Supprimer définitivement "${label || 'Pièce'}" ?`)) return;
        try {
            await deleteEmployeeFile(employee.id, fileId);
            await refresh();
        } catch (e) {
            console.error('Suppression pièce jointe échouée:', e);
            alert('Impossible de supprimer cette pièce jointe.');
        }
    };

    if (loading) {
        return (
            <div className={`flex h-screen bg-gradient-to-br from-slate-50 to-slate-100 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
                <Sidebar />
                <div className="flex-1 flex flex-col p-6 overflow-auto">
                    <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                    <div className="flex justify-center items-center h-full">
                        <span className="loading loading-spinner loading-lg text-[#FDB913]"></span>
                    </div>
                </div>
                <NotificationPanel isOpen={isNotifPanelOpen} onClose={() => setIsNotifPanelOpen(false)} />
            </div>
        );
    }

    if (error || !employee) {
        return (
            <div className={`flex h-screen bg-gradient-to-br from-slate-50 to-slate-100 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
                <Sidebar />
                <div className="flex-1 flex flex-col p-6 overflow-auto">
                    <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                    <div className="flex justify-center items-center h-full">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-error">{error || 'Employé non trouvé'}</h2>
                            <button className="btn btn-warning mt-4" onClick={() => navigate('/employees')}>
                                Retour à la liste
                            </button>
                        </div>
                    </div>
                </div>
                <NotificationPanel isOpen={isNotifPanelOpen} onClose={() => setIsNotifPanelOpen(false)} />
            </div>
        );
    }

    return (
        <div className={`flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
            <Sidebar />

            <div className="flex-1 flex flex-col p-6 overflow-y-auto">
                <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />

                {/* Header avec gradient et retour */}
                <div className="mb-6">
                    <div className="flex items-center gap-4 mb-4">
                        <button
                            className="btn btn-circle btn-ghost hover:bg-[#FDB913] hover:bg-opacity-20"
                            onClick={() => navigate(`/employees/${employee.id}`)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-4xl font-extrabold text-[#FDB913]">
                                Archives
                            </h1>
                            <p className="text-lg text-slate-600 mt-1">{employee.full_name}</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Section Documents officiels */}
                    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-[#FDB913] to-[#E5A511] p-6">
                            <h2 className="text-2xl font-bold text-black flex items-center gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Documents officiels
                            </h2>
                            <p className="text-black text-opacity-80 mt-1">Pièces d'identité et documents administratifs</p>
                        </div>

                        <div className="p-6 space-y-4">
                            {legacyArchives.length === 0 ? (
                                <div className="text-center py-12 text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-lg">Aucun document disponible</p>
                                </div>
                            ) : (
                                legacyArchives.map(doc => (
                                    <div key={doc.field} className="group bg-gradient-to-r from-slate-50 to-white border-2 border-slate-200 rounded-xl p-4 hover:border-[#FDB913] hover:shadow-md transition-all duration-200">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#FDB913]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                    </svg>
                                                    <h3 className="font-bold text-slate-800 text-lg">{doc.label}</h3>
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                                                    {doc.size && (
                                                        <span className="bg-slate-200 px-2 py-1 rounded-full">📦 {doc.size}</span>
                                                    )}
                                                    {doc.date && (
                                                        <span className="bg-slate-200 px-2 py-1 rounded-full">📅 {doc.date}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    className="btn btn-sm bg-[#FDB913] hover:bg-[#E5A511] text-black border-0"
                                                    onClick={() => handleOpenOrPrint(doc.url)}
                                                    title="Ouvrir/Imprimer"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    className="btn btn-sm bg-red-600 hover:bg-red-700 text-white border-0"
                                                    onClick={() => handleDeleteArchive(doc.field)}
                                                    title="Supprimer"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Section Pièces jointes */}
                    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-[#FDB913] to-[#E5A511] p-6">
                            <h2 className="text-2xl font-bold text-black flex items-center gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                Pièces jointes
                            </h2>
                            <p className="text-black text-opacity-80 mt-1">Contrats, avenants et autres documents</p>
                        </div>

                        <div className="p-6 space-y-4 max-h-[500px] overflow-y-auto">
                            {files && files.length > 0 ? (
                                files.map((item) => (
                                    <div key={item.id} className="group bg-gradient-to-r from-slate-50 to-white border-2 border-slate-200 rounded-xl p-4 hover:border-[#FDB913] hover:shadow-md transition-all duration-200">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#FDB913]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <h3 className="font-bold text-slate-800">{item.label || 'Pièce jointe'}</h3>
                                                    <span className="badge bg-[#FDB913] bg-opacity-20 text-[#E5A511] border-0 text-xs">{item.category}</span>
                                                </div>
                                                <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                                    <span className="bg-slate-200 px-2 py-1 rounded-full">
                                                        📦 {item.size || 'Taille inconnue'}
                                                    </span>
                                                    <span className="bg-slate-200 px-2 py-1 rounded-full">
                                                        📅 {new Date(item.uploaded_at).toLocaleDateString('fr-FR')}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    className="btn btn-sm bg-[#FDB913] hover:bg-[#E5A511] text-black border-0"
                                                    onClick={() => handleOpenOrPrint(item.file)}
                                                    title="Ouvrir/Imprimer"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    className="btn btn-sm bg-red-600 hover:bg-red-700 text-white border-0"
                                                    onClick={() => handleDeleteAttached(item.id, item.label)}
                                                    title="Supprimer"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12 text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    <p className="text-lg">Aucune pièce jointe</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Section Upload */}
                <div className="mt-6 bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-[#FDB913] to-[#E5A511] p-6">
                        <h2 className="text-2xl font-bold text-black flex items-center gap-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Ajouter une pièce jointe
                        </h2>
                        <p className="text-black text-opacity-80 mt-1">Téléversez des documents PDF (max 100 MB)</p>
                    </div>

                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold text-slate-700">Fichier PDF</span>
                                </label>
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    className="file-input file-input-bordered w-full border-[#FDB913] focus:border-[#E5A511]"
                                    onChange={(e) => setNewFile(e.target.files?.[0] || null)}
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold text-slate-700">Libellé</span>
                                </label>
                                <input
                                    type="text"
                                    value={newLabel}
                                    onChange={(e) => setNewLabel(e.target.value)}
                                    className="input input-bordered w-full"
                                    placeholder="Ex: Contrat signé"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-semibold text-slate-700">Catégorie</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value as EmployeeFileCategory)}
                                >
                                    <option value="IDENTITY">Identité</option>
                                    <option value="BIRTH">Naissance</option>
                                    <option value="CONTRACT">Contrat</option>
                                    <option value="OTHER">Autre</option>
                                </select>
                            </div>

                            <div className="form-control">
                                <button
                                    className={`btn bg-[#FDB913] hover:bg-[#E5A511] text-black border-0 w-full ${uploading ? 'loading' : ''}`}
                                    onClick={handleUpload}
                                    disabled={uploading || !newFile || !employee}
                                >
                                    {uploading ? (
                                        'Envoi...'
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            Ajouter
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <NotificationPanel isOpen={isNotifPanelOpen} onClose={() => setIsNotifPanelOpen(false)} />
        </div>
    );
};

export default ArchivesPage;
